/**
 * Reconcile a fresh /api/badges payload against the set of categories the
 * user just acked but whose server-side lastSeenAt hasn't propagated yet.
 *
 * Why (Feb 2026): a stale in-flight badge poll can land AFTER the ack POST
 * but BEFORE the server sees the updated lastSeenAt. Without this, the
 * badge would flicker back on for the length of one poll cycle. Extracted
 * from context/NotificationProvider.jsx so the unit tests exercise the
 * exact reducer used in production.
 *
 * @param {Object} badgeData    Latest /api/badges payload keyed by category.
 * @param {Set<string>} pending Categories the caller has optimistically
 *                              cleared and is waiting for the server to
 *                              catch up on.
 * @returns {{badges: Object, pending: Set<string>}} A fresh badges object
 *          (safe to setState with) and a NEW pending Set with any acks
 *          the server has now caught up on removed. The caller can adopt
 *          the returned Set as the new suppression cursor.
 */
export function reconcilePendingAcks(badgeData, pending) {
  const merged = { ...(badgeData || {}) };
  const nextPending = new Set(pending);
  nextPending.forEach((cat) => {
    if (Number(merged[cat]) > 0) {
      // Server hasn't caught up yet — hold the ack.
      merged[cat] = 0;
    } else {
      // Server now reflects the ack — stop suppressing so a genuinely
      // new item is free to badge again on the next poll.
      nextPending.delete(cat);
    }
  });
  return { badges: merged, pending: nextPending };
}
